import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { AdminSidebar } from "@/components/sidebars";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";

const fmtBytes = (b) => {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("es-MX") : "—";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [backups, setBackups] = useState([]);
  const [backupBusy, setBackupBusy] = useState(false);

  const loadHealth = () => {
    apiClient.get("/admin/db/health").then(({ data }) => setHealth(data)).catch(() => {});
    apiClient.get("/admin/db/backups").then(({ data }) => setBackups(data)).catch(() => {});
  };

  useEffect(() => {
    apiClient.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    loadHealth();
  }, []);

  const handleBackupNow = async () => {
    setBackupBusy(true);
    try {
      await apiClient.post("/admin/db/backup");
      toast.success("Backup creado correctamente");
      loadHealth();
    } catch (e) {
      toast.error("Error al crear backup");
    } finally {
      setBackupBusy(false);
    }
  };

  const handleDownload = (filename) => {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    window.open(`${apiBase}/api/admin/db/backups/${encodeURIComponent(filename)}`, "_blank");
  };

  const connPct = health?.connections?.current && health?.connections?.available
    ? Math.round((health.connections.current / (health.connections.current + health.connections.available)) * 100)
    : 0;
  const connHealthy = connPct < 80;

  return (
    <AppLayout sidebar={AdminSidebar}>
      <header className="app-header"><h1>Panel de Administración</h1></header>

      <section className="cards-row">
        <div className="stat-card" data-testid="adm-stat-members"><span className="stat-label">Miembros totales</span><strong className="stat-value">{stats?.total_members ?? "—"}</strong></div>
        <div className="stat-card stat-ok" data-testid="adm-stat-active"><span className="stat-label">Membresías activas</span><strong className="stat-value">{stats?.active_members ?? "—"}</strong></div>
        <div className="stat-card stat-warn" data-testid="adm-stat-pending"><span className="stat-label">Solicitudes pendientes</span><strong className="stat-value">{stats?.pending_solicitudes ?? "—"}</strong></div>
        <div className="stat-card" data-testid="adm-stat-camino"><span className="stat-label">En camino</span><strong className="stat-value">{stats?.en_camino ?? "—"}</strong></div>
        <div className="stat-card" data-testid="adm-stat-paid"><span className="stat-label">Pagos confirmados</span><strong className="stat-value">{stats?.total_paid_transactions ?? "—"}</strong></div>
      </section>

      {/* ===== DATABASE HEALTH ===== */}
      <div className="panel" data-testid="db-health-panel">
        <h2 style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 0 }}>
          <i className="fas fa-database" style={{ color: "#163680" }} />
          Salud de la base de datos
        </h2>

        {!health ? <p className="text-muted">Cargando…</p> : (
          <>
            <div className="cards-row" style={{ marginTop: 14 }}>
              <div className="stat-card">
                <span className="stat-label">Documentos</span>
                <strong className="stat-value">{health.documents_total?.toLocaleString("es-MX") ?? "—"}</strong>
                <small>en {health.collections} colecciones</small>
              </div>
              <div className="stat-card">
                <span className="stat-label">Tamaño datos</span>
                <strong className="stat-value" style={{ fontSize: 22 }}>{fmtBytes(health.data_size_bytes)}</strong>
                <small>+ {fmtBytes(health.indexes_size_bytes)} en índices</small>
              </div>
              <div className={`stat-card ${connHealthy ? "stat-ok" : "stat-warn"}`}>
                <span className="stat-label">Conexiones</span>
                <strong className="stat-value">{health.connections?.current}<small style={{ fontSize: 13, color: "#9ca3af" }}>/{health.connections?.current + (health.connections?.available || 0)}</small></strong>
                <small>{connPct}% en uso</small>
              </div>
              <div className="stat-card">
                <span className="stat-label">Uptime</span>
                <strong className="stat-value" style={{ fontSize: 20 }}>
                  {health.uptime_seconds ? Math.floor(health.uptime_seconds / 3600) + "h" : "—"}
                </strong>
                <small>desde último reinicio</small>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ===== BACKUPS ===== */}
      <div className="panel" data-testid="db-backups-panel">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <i className="fas fa-archive" style={{ color: "#7DC242" }} />
            Backups
            <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}>(retención: {health?.backups?.retention_days || 7} días, automáticos cada 24h)</span>
          </h2>
          <button onClick={handleBackupNow} disabled={backupBusy} className="btn btn-primary" data-testid="btn-backup-now">
            {backupBusy ? "Creando…" : <><i className="fas fa-download" /> Crear backup ahora</>}
          </button>
        </div>

        {backups.length === 0 ? (
          <p className="text-muted">Sin backups todavía. Crea el primero con el botón ↑</p>
        ) : (
          <table className="data-table">
            <thead><tr><th>Archivo</th><th>Tamaño</th><th>Creado</th><th></th></tr></thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.filename} data-testid={`backup-row-${b.filename}`}>
                  <td><code style={{ fontSize: 12 }}>{b.filename}</code></td>
                  <td>{fmtBytes(b.size_bytes)}</td>
                  <td><small>{fmtDate(b.created_at)}</small></td>
                  <td>
                    <button onClick={() => handleDownload(b.filename)} className="btn-mini" data-testid={`download-${b.filename}`}>
                      <i className="fas fa-download" /> Descargar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  );
}
